import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { http } from "../lib/api";
import { Seo } from "../components/Seo";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { formatApiError } from "../context/AuthContext";
import {
  Shield,
  CheckCircle2,
  AlertCircle,
  Copy,
  ArrowRight,
  Info,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

const ISSUE_TYPES = [
  { v: "loan_harassment", l: "Loan recovery harassment / threatening calls" },
  { v: "wrong_cibil", l: "Wrong / disputed CIBIL entry" },
  { v: "unauthorised_charges", l: "Unauthorised charges / deductions" },
  { v: "sarfaesi_notice", l: "SARFAESI / legal notice received" },
  { v: "grievance_unanswered", l: "Grievance not answered for 30+ days" },
  { v: "recovery_agent_misconduct", l: "Recovery agent misconduct" },
  { v: "other", l: "Other" },
];

/* ---------- tiny institution-typeahead ---------- */
const useInstitutionLookup = (query) => {
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      http
        .get("/search/suggest", { params: { q: query, limit: 6 } })
        .then((r) => setResults(r.data.suggestions || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return results;
};

export default function Resolve() {
  const location = useLocation();
  const [params] = useSearchParams();
  const mountedAt = useRef(Date.now());

  // Pre-fill from query: ?institution=HDFC%20Bank&slug=bank-hdfc-bank
  const prefillName = params.get("institution") || location.state?.institutionName || "";
  const prefillSlug = params.get("slug") || location.state?.institutionSlug || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    institution_name: prefillName,
    institution_slug: prefillSlug,
    loan_ref: "",
    issue_type: "loan_harassment",
    description: "",
    consent: false,
    website: "", // honeypot
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // { ticket_no }

  const [lookupOpen, setLookupOpen] = useState(false);
  const lookup = useInstitutionLookup(form.institution_name);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const descCount = form.description.length;
  const descOk = descCount >= 30 && descCount <= 3000;
  const canSubmit =
    form.name.trim().length >= 2 &&
    /@/.test(form.email) &&
    /\d{6,}/.test(form.phone.replace(/\D/g, "")) &&
    form.institution_name.trim().length >= 2 &&
    descOk &&
    form.consent &&
    !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    setBusy(true);
    try {
      const body = { ...form, elapsed_ms: Date.now() - mountedAt.current };
      const { data } = await http.post("/complaints", body);
      setDone(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message || "Submission failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyTicket = () => {
    if (!done?.ticket_no) return;
    navigator.clipboard?.writeText(done.ticket_no);
    toast.success("Ticket number copied");
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] flex flex-col">
      <Seo
        title="File a Complaint Against Any Bank or NBFC in India — Free Resolution Help"
        description="Free, confidential complaint resolution for issues with any Indian Bank, NBFC, ARC or HFC. Recovery harassment, CIBIL disputes, mis-selling, KYC, frozen accounts — submit your issue to verified Nodal channels."
        path="/resolve"
        keywords="bank complaint india, nbfc complaint, file rbi complaint, recovery agent harassment, cibil dispute resolution"
      />
      <SiteHeader />

      <main className="flex-1" data-testid="resolve-page">
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 md:py-20 grid lg:grid-cols-[1fr_360px] gap-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#002FA7] mb-3">
                Resolve · Step 01 / 01
              </div>
              <h1
                className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.98] max-w-2xl"
                data-testid="resolve-title"
              >
                Stuck with a bank<br />or NBFC?
              </h1>
              <p className="mt-5 max-w-xl text-base md:text-lg text-[#4B5563] leading-relaxed">
                Tell us what happened. A BankDesks operator reviews every
                submission and pings the institution's nodal officer using the
                contacts we've already indexed.
              </p>
            </div>
            <aside className="border border-[#0A0A0A] p-5 text-sm bg-[#F8F9FA]">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 mt-1 text-[#002FA7]" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-1">
                    Privacy
                  </div>
                  <p className="text-[#0A0A0A] leading-relaxed">
                    Your submission is private and will only be reviewed by
                    <strong> BankDesks operators</strong>. We are not a
                    regulator — for formal complaints escalate to RBI CMS
                    after 30 days of no response.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Success screen */}
        {done ? (
          <section className="border-b border-[#0A0A0A]">
            <div className="max-w-[700px] mx-auto px-6 md:px-10 py-20 text-center" data-testid="resolve-success">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600 text-white mb-5">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
                submission received
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">
                We've got it.
              </h2>
              <p className="text-[#4B5563] max-w-md mx-auto">
                Your request is queued for review. Please keep this ticket
                number for future reference — you can quote it in any follow-up.
              </p>
              <div className="mt-6 inline-flex items-center gap-3 border-2 border-[#0A0A0A] px-5 py-3">
                <span
                  className="font-mono text-2xl font-black tracking-wider"
                  data-testid="resolve-ticket-no"
                >
                  {done.ticket_no}
                </span>
                <button
                  onClick={copyTicket}
                  className="text-[#4B5563] hover:text-[#0A0A0A]"
                  data-testid="resolve-ticket-copy"
                  title="Copy"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-10">
                <Link
                  to="/"
                  data-testid="resolve-back-home"
                  className="inline-flex items-center gap-2 border border-[#0A0A0A] px-5 py-3 text-sm font-bold hover:bg-[#0A0A0A] hover:text-white"
                >
                  Back to directory <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        ) : (
          /* Form */
          <section>
            <form
              onSubmit={onSubmit}
              className="max-w-[900px] mx-auto px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5"
              data-testid="resolve-form"
            >
              {/* Honeypot (hidden from humans but visible to bots) */}
              <div aria-hidden="true" className="absolute -left-[9999px]">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => set("website", e.target.value)}
                  />
                </label>
              </div>

              <Field label="Your full name" required>
                <input
                  type="text" required value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="resolve-name"
                  className="brutal-input"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email" required value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="resolve-email"
                  className="brutal-input"
                />
              </Field>
              <Field label="Phone (with country code)">
                <input
                  type="tel" required value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 98765 43210"
                  data-testid="resolve-phone"
                  className="brutal-input"
                />
              </Field>
              <Field label="Loan / account ref (optional)">
                <input
                  type="text" value={form.loan_ref}
                  onChange={(e) => set("loan_ref", e.target.value)}
                  data-testid="resolve-loan-ref"
                  className="brutal-input"
                />
              </Field>

              {/* Institution typeahead */}
              <div className="relative md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-2">
                  Bank / NBFC in question
                </label>
                <input
                  type="text" required value={form.institution_name}
                  onChange={(e) => { set("institution_name", e.target.value); set("institution_slug", ""); setLookupOpen(true); }}
                  onFocus={() => setLookupOpen(true)}
                  onBlur={() => setTimeout(() => setLookupOpen(false), 150)}
                  placeholder="Start typing — e.g. Bajaj Finance"
                  data-testid="resolve-institution"
                  className="brutal-input"
                />
                {lookupOpen && lookup.length > 0 && (
                  <ul
                    className="absolute z-10 left-0 right-0 mt-1 border border-[#0A0A0A] bg-white max-h-[280px] overflow-y-auto shadow-lg"
                    data-testid="resolve-institution-suggestions"
                  >
                    {lookup.map((s) => (
                      <li key={s.slug}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            set("institution_name", s.name);
                            set("institution_slug", s.slug);
                            setLookupOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-[#0A0A0A] hover:text-white flex items-center justify-between gap-2"
                        >
                          <span className="font-bold text-sm">{s.name}</span>
                          <span className="font-mono text-[10px] opacity-70">
                            {s.category} · {s.hq_city}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Field label="Issue type" wide>
                <select
                  required value={form.issue_type}
                  onChange={(e) => set("issue_type", e.target.value)}
                  data-testid="resolve-issue-type"
                  className="brutal-input"
                >
                  {ISSUE_TYPES.map((t) => (
                    <option key={t.v} value={t.v}>{t.l}</option>
                  ))}
                </select>
              </Field>

              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] flex items-center justify-between mb-2">
                  <span>What happened? (30–3000 chars)</span>
                  <span className={descCount > 3000 ? "text-[#FF3333]" : "text-[#4B5563]"}>
                    {descCount}/3000
                  </span>
                </label>
                <textarea
                  required rows={8} minLength={30} maxLength={3000}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  data-testid="resolve-description"
                  placeholder="Include dates, amounts, names, any calls received, and what response (or silence) you got so far. The more specific, the faster we can escalate."
                  className="brutal-input resize-y"
                />
                {!descOk && descCount > 0 && (
                  <div className="mt-1 text-[11px] text-[#FF3333] font-mono">
                    {descCount < 30 ? `${30 - descCount} more character${30 - descCount === 1 ? "" : "s"} needed` : "Too long"}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 flex items-start gap-2">
                <input
                  id="consent" type="checkbox" required
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  data-testid="resolve-consent"
                  className="mt-1 w-4 h-4"
                />
                <label htmlFor="consent" className="text-xs text-[#4B5563] leading-relaxed">
                  I understand this submission is private and will only be
                  reviewed by <strong>BankDesks operators</strong>. I consent
                  to being contacted on the details above for follow-up.
                </label>
              </div>

              {err && (
                <div className="md:col-span-2 flex items-start gap-2 border-l-4 border-[#FF3333] bg-red-50 px-3 py-2 text-xs text-red-900">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{err}</span>
                </div>
              )}

              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-4 border-t border-[#E5E7EB]">
                <div className="text-[11px] text-[#4B5563] font-mono flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Rate-limited · 3 submissions / hour / IP
                </div>
                <button
                  type="submit" disabled={!canSubmit}
                  data-testid="resolve-submit"
                  className="bg-[#0A0A0A] text-white font-bold px-6 py-3 text-sm tracking-wide hover:bg-[#002FA7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "Submitting…" : "Submit request"}
                </button>
              </div>
            </form>
          </section>
        )}
      </main>
      <SiteFooter />

      <style>{`
        .brutal-input {
          width: 100%;
          border: 1px solid #0A0A0A;
          background: #fff;
          padding: 10px 12px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
          line-height: 1.4;
        }
        .brutal-input:focus {
          outline: none;
          box-shadow: 0 0 0 2px #002FA7;
        }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, wide, children }) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-2">
      {label}{required && <span className="text-[#FF3333] ml-1">*</span>}
    </label>
    {children}
  </div>
);
